import { jsxs, jsx } from 'react/jsx-runtime';
import React__default from 'react';
import { styled } from 'styled-components';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import * as Popover from '@radix-ui/react-popover';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { IconResourceFile } from '@fabric-app/icons/svg';
import { EmbedResourceDropHandlerKey } from '../../../Extensions/EmbedResource/PasteDropPlugin.js';

const Trigger = styled.button.withConfig({
  displayName: "AddResourceToolbarButton__Trigger",
  componentId: "sc-959e403f-0"
})([
  `position:relative;border-radius:6px;border:none;padding:0 0.5rem;display:flex;align-items:center;justify-content:center;height:1.75rem;min-width:1.75rem;font-size:1em;margin:0;outline:none;font-weight:500;color:var(--fabric-color-text-tertiary);background:none;&:hover{color:var(--fabric-color-text-primary);background:rgba(var(--color-foreground),0.1);}&:focus{box-shadow:0 0 0 2px rgb(var(--color-primary));}`
]);
const PopoverContent = styled(Popover.Content).withConfig({
  displayName: "AddResourceToolbarButton__PopoverContent",
  componentId: "sc-959e403f-1"
})([
  `border-radius:12px;padding:16px;width:260px;z-index:125;background-color:var(--fabric-color-bg-primary);box-shadow:rgba(128,128,128,0.25) 0px 10px 38px -5px,rgba(128,128,128,0.2) 0px 10px 20px -8px;will-change:transform,opacity;display:flex;flex-direction:column;gap:16px;flex-shrink:0;@media (max-width:$mobile-width){flex-direction:column-reverse;}pointer-events:auto;&:focus{box-shadow:rgba(128,128,128,0.25) 0px 10px 38px -5px,rgba(128,128,128,0.2) 0px 10px 20px -15px,0 0 0 2px var(--violet-7);}`
]);
const PopoverArrow = styled(Popover.Arrow).withConfig({
  displayName: "AddResourceToolbarButton__PopoverArrow",
  componentId: "sc-959e403f-2"
})([
  `fill:var(--fabric-color-bg-primary);`
]);
const Dropzone = styled.button.withConfig({
  displayName: "AddResourceToolbarButton__Dropzone",
  componentId: "sc-959e403f-3"
})([
  `width:228px;height:228px;display:flex;align-items:center;justify-content:center;border-radius:13px;border:1px dashed var(--fabric-color-border-primary);color:rgba(var(--fabric-color-text-primary-rgb),0.6);cursor:pointer;transition:border-color 0.2s ease-in-out,color 0.2s ease-in-out;&:hover,&[data-hover='true']{border-color:var(--fabric-color-app-primary-text);color:var(--fabric-color-app-primary-text);}`
]);
const ToolbarCustomImageButton = ({ editor }) => {
  const [open, setOpen] = React__default.useState(false);
  const inputRef = React__default.useRef(null);
  const [canUploadImage, setCanUploadImage] = React__default.useState(false);
  React__default.useEffect(() => {
    const onUpdate = ({ editor: editor2 }) => {
      const embedStorage = EmbedResourceDropHandlerKey.getState(editor2.state);
      setCanUploadImage(!!embedStorage?.onUpload);
    };
    editor.on("update", onUpdate);
    return () => {
      editor.off("update", onUpdate);
    };
  }, [
    editor
  ]);
  const handleUploadImage = (file) => {
    if (!canUploadImage) return;
    editor.chain().focus().uploadResource(file);
    setOpen(false);
  };
  const onClickDropZone = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };
  const [filesOver, setFilesOver] = React__default.useState(false);
  const handleDragEnter = (e) => {
    e.preventDefault();
    setFilesOver(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    setFilesOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setFilesOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
    setFilesOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUploadImage(e.dataTransfer.files[0]);
    }
  };
  const { isMobileView } = useResponsive();
  const dropZoneText = React__default.useMemo(() => {
    if (isMobileView) return "Click to upload";
    return filesOver ? "Drop to upload" : "Drop or click to upload";
  }, [
    filesOver,
    isMobileView
  ]);
  return /* @__PURE__ */ jsxs(Popover.Root, {
    open: open && canUploadImage,
    onOpenChange: setOpen,
    children: [
      /* @__PURE__ */ jsx(Popover.Trigger, {
        asChild: true,
        children: /* @__PURE__ */ jsx(Trigger, {
          onTouchStart: (e) => e.stopPropagation(),
          onTouchEnd: (e) => e.stopPropagation(),
          onClick: (e) => e.stopPropagation(),
          children: /* @__PURE__ */ jsx(IconResourceFile, {})
        })
      }),
      /* @__PURE__ */ jsx(Popover.Portal, {
        container: getDefaultRadixPortalContainer(),
        children: /* @__PURE__ */ jsxs(PopoverContent, {
          "data-tiptap-toolbar": true,
          onFocusOutside: (e) => {
            e.preventDefault();
          },
          sideOffset: 10,
          align: "start",
          side: isMobileView ? "top" : "bottom",
          children: [
            /* @__PURE__ */ jsx(Dropzone, {
              "data-hover": filesOver,
              onClick: onClickDropZone,
              onDragEnter: handleDragEnter,
              onDragOver: handleDragOver,
              onDragLeave: handleDragLeave,
              onDrop: handleDrop,
              children: dropZoneText
            }),
            /* @__PURE__ */ jsx("input", {
              type: "file",
              style: {
                display: "none"
              },
              ref: inputRef,
              onChange: (e) => {
                if (e.target.files && e.target.files.length > 0) {
                  handleUploadImage(e.target.files[0]);
                }
              }
            }),
            /* @__PURE__ */ jsx(PopoverArrow, {})
          ]
        })
      })
    ]
  });
};

export { ToolbarCustomImageButton as default };
//# sourceMappingURL=AddResourceToolbarButton.js.map
